import { useEffect, useState } from 'react';

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const LOOP_EXTENSION_IDS = ['NETSCAPE2.0', 'ANIMEXTS1.0'];

const LOOP_SIGNATURE_LENGTH = 14;

const HEADER_PEEK_BYTES = 8192;

const isGif = (bytes) =>
  bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;

const findLoopExtension = (bytes) => {
  for (let i = 0; i <= bytes.length - LOOP_SIGNATURE_LENGTH; i += 1) {
    if (bytes[i] !== 0x21 || bytes[i + 1] !== 0xff || bytes[i + 2] !== 0x0b) {
      continue;
    }

    let identifier = '';

    for (let j = 0; j < 11; j += 1) {
      identifier += String.fromCharCode(bytes[i + 3 + j]);
    }

    if (LOOP_EXTENSION_IDS.includes(identifier)) {
      return i;
    }
  }

  return -1;
};

const peekIsLooping = async (src, signal) => {
  const response = await fetch(src, { signal });

  if (!response.ok) {
    throw new Error(`Failed to load gif: ${response.status}`);
  }

  if (!response.body) {
    return null;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let size = 0;

  try {
    while (size < HEADER_PEEK_BYTES) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      chunks.push(value);
      size += value.length;
    }
  } finally {
    reader.cancel().catch(() => {});
  }

  const head = new Uint8Array(size);
  let offset = 0;

  chunks.forEach((chunk) => {
    head.set(chunk, offset);
    offset += chunk.length;
  });

  if (!isGif(head)) {
    return null;
  }

  return findLoopExtension(head) !== -1;
};

const stripLoopExtension = (buffer) => {
  const bytes = new Uint8Array(buffer);

  if (!isGif(bytes)) {
    return null;
  }

  const start = findLoopExtension(bytes);

  if (start === -1) {
    return null;
  }

  let end = start + LOOP_SIGNATURE_LENGTH;

  while (end < bytes.length && bytes[end] !== 0x00) {
    end += bytes[end] + 1;
  }

  end += 1;

  return [bytes.subarray(0, start), bytes.subarray(end)];
};

const usePlayOnceGif = (src) => {
  const [gifSrc, setGifSrc] = useState(TRANSPARENT_PIXEL);

  useEffect(() => {
    if (!src) {
      return undefined;
    }

    let objectUrl = null;
    let isCancelled = false;
    const controller = new AbortController();

    setGifSrc(TRANSPARENT_PIXEL);

    const fallBackToOriginal = () => {
      if (!isCancelled) {
        setGifSrc(src);
      }
    };

    peekIsLooping(src, controller.signal)
      .then((isLooping) => {
        if (isCancelled) {
          return undefined;
        }

        if (!isLooping) {
          fallBackToOriginal();
          return undefined;
        }

        return fetch(src, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load gif: ${response.status}`);
            }

            return response.arrayBuffer();
          })
          .then((buffer) => {
            if (isCancelled) {
              return;
            }

            const withoutLoop = stripLoopExtension(buffer);

            if (!withoutLoop) {
              fallBackToOriginal();
              return;
            }

            objectUrl = URL.createObjectURL(
              new Blob(withoutLoop, { type: 'image/gif' })
            );
            setGifSrc(objectUrl);
          });
      })
      .catch(() => {
        fallBackToOriginal();
      });

    return () => {
      isCancelled = true;
      controller.abort();

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  return gifSrc;
};

export default usePlayOnceGif;
